<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Button from "../../components/input/Button.svelte";
  import StatusBadge from "../../components/data-display/StatusBadge.svelte";

  type BlogPost = {
    id: string;
    title: string;
    share_token?: string;
    blog_type: string;
    tone: string;
    status: string;
    cover_image_url?: string;
    published_at?: string;
    day_number?: number;
    view_count?: number;
  };

  let { trip, blogPosts = [] }: { trip: any; blogPosts?: BlogPost[] } = $props();

  let filter = $state("all");
  let showGenerator = $state(false);
  let generating = $state(false);
  let generator = $state({ blog_type: "summary", tone: "casual", day_number: "1" });

  const filteredPosts = $derived(
    filter === "all" ? blogPosts : blogPosts.filter((post) => post.status === filter)
  );

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  async function generatePost() {
    generating = true;
    const response = await fetch(`/api/v1/trips/${trip.id}/blog_posts/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify(generator)
    });
    generating = false;
    if (!response.ok) return;
    showGenerator = false;
    router.reload();
  }

  function openEditor(postId: string) {
    router.visit(`/trips/${trip.id}/blog/${postId}/edit`);
  }

  async function copyShareLink(post: BlogPost) {
    if (post.status !== "published" || !post.share_token) return;
    await navigator.clipboard.writeText(`${window.location.origin}/api/v1/blog/share/${post.share_token}`);
  }
</script>

<TripSubNav {trip} currentPage="blog" />

<div class="mx-auto max-w-6xl space-y-8 px-4 py-6 sm:px-6 lg:px-8">
  <section class="rounded-3xl border border-white/10 bg-white/5 p-5">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold">여행 블로그</h1>
        <p class="mt-1 text-sm text-muted">여행 일정, 사진, 경비 데이터를 기반으로 글을 생성하고 발행합니다.</p>
      </div>
      <div class="flex items-center gap-2">
        <Button variant={filter === "all" ? "primary" : "secondary"} size="sm" onclick={() => (filter = "all")}>전체</Button>
        <Button variant={filter === "draft" ? "primary" : "secondary"} size="sm" onclick={() => (filter = "draft")}>초안</Button>
        <Button variant={filter === "published" ? "primary" : "secondary"} size="sm" onclick={() => (filter = "published")}>발행</Button>
        <Button variant="primary" size="sm" onclick={() => (showGenerator = true)}>AI 블로그 만들기</Button>
      </div>
    </div>
  </section>

  <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
    {#if filteredPosts.length === 0}
      <div class="rounded-2xl border border-dashed border-white/10 px-6 py-10 text-center text-sm text-muted">표시할 포스트가 없습니다.</div>
    {/if}

    {#each filteredPosts as post}
      <article class="overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70">
        <button class="block w-full text-left" onclick={() => openEditor(post.id)}>
          <div class="aspect-[16/10] bg-white/5">
            {#if post.cover_image_url}
              <img src={post.cover_image_url} alt={post.title} class="h-full w-full object-cover" />
            {:else}
              <div class="flex h-full items-center justify-center text-5xl text-muted">✍️</div>
            {/if}
          </div>
          <div class="space-y-3 p-4">
            <div class="flex items-center gap-2 text-xs text-muted">
              <span class="rounded-full bg-white/8 px-2 py-0.5">{post.blog_type}</span>
              <span>{post.tone}</span>
              <StatusBadge status={post.status === 'published' ? 'published' : 'draft'} />
            </div>
            <div>
              <h2 class="font-medium">{post.title}</h2>
              <p class="mt-1 text-sm text-muted">
                {post.day_number ? `${post.day_number}일차 · ` : ""}{post.published_at ? new Date(post.published_at).toLocaleDateString("ko-KR") : "미발행"}
              </p>
            </div>
          </div>
        </button>
        <div class="flex items-center justify-between border-t border-white/10 px-4 py-3 text-sm text-muted">
          <span>조회 {post.view_count || 0}</span>
          <Button variant="ghost" size="sm" disabled={post.status !== "published"} onclick={() => copyShareLink(post)}>공유 링크</Button>
        </div>
      </article>
    {/each}
  </div>
</div>

{#if showGenerator}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
    <div class="w-full max-w-md rounded-3xl border border-white/10 bg-slate-950 p-6">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold">AI 블로그 생성</h2>
        <Button variant="ghost" size="sm" onclick={() => (showGenerator = false)}>닫기</Button>
      </div>
      <div class="mt-5 space-y-3">
        <select bind:value={generator.blog_type} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2">
          <option value="daily">daily</option>
          <option value="summary">summary</option>
          <option value="food_review">food_review</option>
          <option value="attraction">attraction</option>
        </select>
        <select bind:value={generator.tone} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2">
          <option value="casual">casual</option>
          <option value="informative">informative</option>
          <option value="emotional">emotional</option>
        </select>
        {#if generator.blog_type === "daily"}
          <input bind:value={generator.day_number} type="number" min="1" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2" placeholder="일차" />
        {/if}
        <Button variant="primary" fullWidth loading={generating} onclick={generatePost}>생성</Button>
      </div>
    </div>
  </div>
{/if}
